
import { computed, inject, onMounted, ref, toRefs, watch } from 'vue';
import { NodeType } from '@amplify/amp-module';
import { cloneDeep } from 'lodash';
import NodeWrapper from '@/components/graph/common/NodeWrapper.vue';
import { isButtonMultiline } from '@/utils/builder-utils';
import { NodeSizes } from '@/constants/builder';

export default {
  components: { NodeWrapper },

  /**
   * id - the UUID of the current sub node
   * index - the index of the sub node inside the group node
   * data - the current sub node's data
   * parentId - the current sub node's parent UUID
   * selected - indicates if the sub node is selected
   * blurred - indicates if the sub node is blurred
   * readonly - indicates if the sub node is readonly
   */
  props: {
    id: { type: String, default: '' },
    index: { type: Number, default: -1 },
    data: { type: Object, default: () => ({}) },
    parentId: { type: String, default: '' },
    selected: { type: Boolean, default: false },
    blurred: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    nodeUiData: { type: Object, default: () => ({}) },
  },

  setup(props, { emit }) {
    const nodeType = NodeType.buttonGroup;
    const nodeActions = [{ click: true, key: 'bottom' }];

    const getNode = inject('getNode', () => {});

    const { selected, nodeUiData } = toRefs(props);

    const hovered = ref(false);
    const selectedButtonText = ref(-1);
    const maxInputWidth = ref('100%');
    const textContainer = ref(null);
    const buttonInput = ref(null);

    const uiData = computed(() => ({ hovered: hovered.value, selected: selected.value }));
    const selectedButtonTextLength = computed(() => (props.data.buttonTexts[selectedButtonText.value] || '').length);
    const autosizeOptions = computed(() => ({
      maxWidth: maxInputWidth.value,
      minWidth: '0',
      minHeight: '16px',
      comfortZone: 0,
    }));

    const nodeHeights = computed(() => {
      const parentNode = getNode();
      const parentNodeType = parentNode.shape.split('-')[0];
      const defaultHeight = NodeSizes[parentNodeType].height;
      const buttonGroupHeights = [defaultHeight, defaultHeight + 16];
      const questionHeights = [defaultHeight, defaultHeight + 32, defaultHeight + 16];

      return parentNodeType === NodeType.buttonGroup ? buttonGroupHeights : questionHeights;
    });

    watch(selected, (value) => {
      const parentNode = getNode();
      const { width, height } = parentNode.size();
      const newHeight = value ? height + 16 : height - 16;
      const isMultiline = isButtonMultiline(props.data.buttonTexts);

      if (isMultiline || nodeHeights.value.includes(height)) {
        parentNode.resize(width, newHeight);
      }
    });

    watch(selectedButtonText, (index, oldIndex) => {
      if (index !== oldIndex && (index === -1 || oldIndex === -1)) {
        const parentNode = getNode();
        const { height } = parentNode.size();
        const isMultiline = isButtonMultiline(props.data.buttonTexts);

        if (isMultiline || nodeHeights.value.includes(height)) {
          setMaxInputWidth();
        }
      }
    });

    watch(() => nodeUiData.value.hovered, (nodeHovered) => {
      if (!nodeHovered) {
        uiData.value.hovered = false;
      }
    });

    onMounted(() => {
      setMaxInputWidth();
    });

    const addButtonText = () => {
      if (props.readonly) {
        return;
      }

      const buttons = cloneDeep(props.data.buttonTexts);
      emit('update-node-data', { ...props.data, buttonTexts: [...buttons, ''] });
    };

    const onDeleteButtonText = (index) => {
      if (props.readonly) {
        return;
      }

      const buttons = cloneDeep(props.data.buttonTexts);
      buttons.splice(index, 1);
      emit('update-node-data', { ...props.data, buttonTexts: buttons });
    };

    const onButtonTextChange = (e, index) => {
      if (props.readonly) {
        return;
      }

      const buttons = cloneDeep(props.data.buttonTexts);
      buttons.splice(index, 1, e.target.value);
      emit('update-node-data', { ...props.data, buttonTexts: buttons });
    };

    const onButtonTextKeyDown = (e) => {
      // Disable newline in button texts
      if (e.keyCode === 13 || e.key.toLowerCase() === 'enter') {
        e.preventDefault();
      }
    };

    const setMaxInputWidth = () => {
      const containerWidth = textContainer.value?.offsetWidth;
      const [input] = buttonInput.value || [];

      if (input) {
        const buttonParent = input.closest('.button-text');
        const inputOffset = buttonParent ? buttonParent.offsetWidth - input.offsetWidth : 0;
        maxInputWidth.value = `${containerWidth - inputOffset}px`;
      } else {
        maxInputWidth.value = '100%';
      }
    };

    return {
      autosizeOptions,
      buttonInput,
      emit,
      hovered,
      nodeType,
      nodeActions,
      selectedButtonText,
      selectedButtonTextLength,
      textContainer,
      uiData,
      addButtonText,
      onButtonTextChange,
      onButtonTextKeyDown,
      onDeleteButtonText,
    };
  },
};
